import React from 'react';
import { Table } from 'reactstrap';
import { PropTypes } from 'prop-types';
import Currency from 'components/Currency';
import moment from 'moment';
import styled from 'styled-components';
import get from 'lodash/get';

const StyledTable = styled(Table)`
  margin: 0;
  padding: 0;

  td {
    padding-top: 0.3em;
    padding-bottom: 0.3em;
    vertical-align: middle;
    white-space: nowrap;

    del,
    span {
      vertical-align: middle;
    }

    del {
      padding-right: 0.2em;
    }
  }

  td:first-child {
    border-right: 1px solid #ddd;
  }

  td:last-child {
    text-align: right;
  }

  td:last-child span {
    font-weight: bold;
  }

  td:last-child del span {
    font-weight: normal;
  }

  tr:last-child {
    td {
      color: #fff;
    }

    td:last-child {
      span {
        color: ${props => props.theme.h5.color};
        font-size: 2.5em;

        @media screen and (max-width: ${props =>
            props.theme.breakpoints.small}) {
          font-size: 1.5em;
        }
      }

      del,
      del span {
        color: #000;
        font-size: 1em;
      }
    }
  }

  td.nightly-average {
    span {
      color: ${props => props.theme.a.color};
    }

    del span {
      color: #000;
    }
  }

  th {
    font-size: 1.2em;
    color: ${props => props.theme.h5.color};

    &:last-child {
      text-align: right;
    }

    span {
      font-size: 1em;
    }
  }
`;

export function ChargesSummary({
  room,
  originalSubtotal,
  originalTotal,
  subTotal,
  total,
  nightlyAverage,
  taxesAndFees,
  confirmation,
  discount
}) {
  if (!room) {
    return null;
  }

  const nightlyCharges = room.minRoomRate.expectedCharges.chargesForPostingDate;
  const originalExpectedCharges = get(
    room,
    'rackRoomRate.expectedCharges.chargesForPostingDate',
    null
  );
  const originalNightlyAverage = originalSubtotal
    ? originalSubtotal / nightlyCharges.length
    : '';

  return (
    <StyledTable striped borderless>
      {!isNaN(total) && confirmation && (
        <thead>
          <tr>
            <th>SUMMARY</th>
            <th>
              {originalTotal && (
                <del>
                  <Currency
                    value={originalTotal}
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                  />
                </del>
              )}
              {discount && (
                <Currency
                  value={discount}
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                />
              )}
              <Currency
                value={total}
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </th>
          </tr>
        </thead>
      )}
      <tbody>
        {nightlyCharges.map((charge, i) => {
          const originalTotalCharges = originalExpectedCharges
            ? originalExpectedCharges[i].roomRateAndPackages.totalCharges
            : 0;

          return (
            <tr key={i}>
              <td>
                {moment(charge.postingDate, 'YYYY-MM-DD').format('dddd MMM D')}
              </td>
              <td>
                {originalTotalCharges >
                  charge.roomRateAndPackages.totalCharges && (
                  <del>
                    <Currency
                      value={originalTotalCharges}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </del>
                )}
                <Currency
                  value={charge.roomRateAndPackages.totalCharges}
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              </td>
            </tr>
          );
        })}
        {!!nightlyAverage && (
          <tr>
            <td>Nightly Avg.</td>
            <td className="nightly-average">
              {originalNightlyAverage && (
                <del>
                  <Currency
                    value={originalNightlyAverage}
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </del>
              )}
              <Currency
                value={nightlyAverage}
                minimumFractionDigits={0}
                maximumFractionDigits={0}
              />
            </td>
          </tr>
        )}
        {!isNaN(taxesAndFees) && (
          <tr>
            <td>TAX</td>
            <td>
              <Currency
                value={taxesAndFees}
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </td>
          </tr>
        )}
        {!isNaN(subTotal) && (
          <tr>
            <td>
              <h5>SUBTOTAL</h5>
            </td>
            <td>
              {originalSubtotal && (
                <del>
                  <Currency
                    value={originalSubtotal}
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                  />
                </del>
              )}
              <Currency
                value={subTotal}
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </td>
          </tr>
        )}
        {!isNaN(total) && !confirmation && (
          <tr>
            <td>TOTAL</td>
            <td>
              {originalTotal && (
                <del>
                  <Currency
                    value={originalTotal}
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                  />
                </del>
              )}
              <Currency
                value={total}
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </td>
          </tr>
        )}
      </tbody>
    </StyledTable>
  );
}

ChargesSummary.propTypes = {
  room: PropTypes.object,
  nightlyAverage: PropTypes.number,
  originalSubtotal: PropTypes.number,
  originalTotal: PropTypes.number,
  subTotal: PropTypes.number,
  total: PropTypes.number,
  taxesAndFees: PropTypes.number,
  confirmation: PropTypes.bool,
  discount: PropTypes.number
};

export default ChargesSummary;
